import { useWeb3React } from "@web3-react/core";
import { Dispatch, SetStateAction, useCallback, useMemo } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import styled from "styled-components";
import Form from "react-bootstrap/Form";
import { Body1, Body2, Heading3, Heading6 } from "../../components/Typography";
import { PAYMENT_METHOD_ETH, TOKEN_PRECISIONS, TOKENS } from "../../constants";
import { COLOR_WHITE, COLOR_YELLOW } from "../../constants/colors";
import copyIcon from "../../images/icons/ic-copy.svg";
import logoEth from "../../images/logo-eth.png";
import { floor, getProviderName, useBalance } from "../../utils";
import { PaymentImage, PaymentMethod } from "./shared";

const PaymentNameWrapper = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;

  @media (max-width: 425px) {
    flex-direction: column;
    align-items: unset;
    gap: 5px;
  }
`;

const TokenSelect = styled(Form.Select)`
  height: fit-content;
  width: fit-content;
`;

const Container = styled(PaymentMethod)`
  background: linear-gradient(245.96deg, #141414 0%, #333333 43.94%, #8c8c8c 122.57%);
  display: flex;
  flex-direction: column;
  gap: 20px;
  color: ${COLOR_WHITE};
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 768px) {
    gap: 15px;
  }
`;

const PaymentName = styled.div`
  ${Heading3}
`;

const PaymentNameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  width: 100%;
`;

const WalletAddress = styled.span`
  ${Body1}
`;

const ConnectionDetailsContainer = styled.div`
  display: flex;
  gap: 66px;

  @media (max-width: 425px) {
    flex-direction: column;
    gap: 20px;
  }
`;

const ConnectionDetails = styled.div`
  display: flex;
  flex-direction: column;

  & > div {
    font-weight: bold;
  }
`;

const DetailName = styled.div`
  ${Body2}
`;

const Detail = styled.div`
  ${Heading6}
  white-space: nowrap;
`;

const ChangeWalletButton = styled.div`
  ${Body2}
  color: ${COLOR_YELLOW} !important;
  text-decoration: underline;
  cursor: pointer;
  white-space: nowrap;
`;

const CopyIcon = styled.img`
  cursor: pointer;

  width: 14px;

  &:active {
    transform: scale(0.9);
  }
`;

type Props = {
  setPaymentMethod: Dispatch<SetStateAction<string>>;
  selectedPayment: string;
  setIsWeb3ConnectModalOpen: Dispatch<SetStateAction<boolean>>;
  token: string;
  setToken: Dispatch<SetStateAction<string>>;
};

export default function CryptoPaymentMethod({
  setPaymentMethod,
  selectedPayment,
  setIsWeb3ConnectModalOpen,
  token,
  setToken,
}: Props) {
  const { account, deactivate, library } = useWeb3React();

  const balance = floor(useBalance(token), TOKEN_PRECISIONS[token]);

  const displayedAccount = useMemo(() => {
    if (!account) return "";
    const firstSubtring = account.substring(0, 6);
    const lastSubstring = account.substring(account.length - 5, account.length);
    return `${firstSubtring}...${lastSubstring}`;
  }, [account]);

  const isSelected = useMemo(() => {
    return selectedPayment === PAYMENT_METHOD_ETH;
  }, [selectedPayment]);

  const onChangeWallet = useCallback(() => {
    deactivate();
    setIsWeb3ConnectModalOpen(true);
  }, [deactivate, setIsWeb3ConnectModalOpen]);

  return (
    <Container isSelected={isSelected} onClick={() => setPaymentMethod(PAYMENT_METHOD_ETH)}>
      <PaymentNameContainer>
        <PaymentImage src={logoEth} alt="Ethereum" />
        <div>
          <PaymentNameWrapper>
            <PaymentName>Crypto</PaymentName>
            {isSelected && account && (
              <TokenSelect value={token} onChange={(e: any) => setToken(e.target.value)}>
                <option value={`${TOKENS.ETH}`}>{TOKENS.ETH}</option>
                <option value={`${TOKENS.USDT}`}>{TOKENS.USDT}</option>
              </TokenSelect>
            )}
          </PaymentNameWrapper>
          {isSelected && account && (
            <>
              <WalletAddress>{displayedAccount}</WalletAddress>
              &nbsp;
              <CopyToClipboard text={account || ""}>
                <CopyIcon src={copyIcon} alt="Copy" />
              </CopyToClipboard>
            </>
          )}
        </div>
      </PaymentNameContainer>

      {isSelected && account && (
        <ConnectionDetailsContainer>
          <ConnectionDetails>
            <DetailName>Wallet Connected</DetailName>
            <Detail className="mb-2">{getProviderName(library?.connection?.url)}</Detail>
            <ChangeWalletButton onClick={onChangeWallet}>Change Wallet</ChangeWalletButton>
          </ConnectionDetails>
          <ConnectionDetails>
            <DetailName>Balance</DetailName>
            <Detail>
              {balance} {token}
            </Detail>
          </ConnectionDetails>
        </ConnectionDetailsContainer>
      )}
    </Container>
  );
}
